import { get, reduce, forEach, filter, mapValues } from 'lodash-es';
import { PARTNERS } from 'site-modules/shared/constants/multi-offer/partners';
import { VEHICLE_CONDITION_CQ_ID } from 'client/data/constants/multi-offer';

export const CHANGE_ACTION_TYPE = {
  ADD: 'add',
  REMOVE: 'remove',
};

export const CONDITION_QUESTION_ANSWER_TYPE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

/**
 * Answers to use for Carwiser questions with multiple answer type.
 */
export const CARWISER_MULTIPLE_ANSWERS = {
  UNSELECTED: {
    id: '1',
    desc: 'No',
  },
  SELECTED: {
    id: '2',
    desc: 'Yes',
  },
};

/**
 * Returns a default answer by:
 * * the answer type of the question;
 * * all the question answers.
 * @param {String} answerType
 * @param {Array} answers
 * @returns Empty object {} or the first answer id
 */
export const getDefaultAnswer = (answerType, answers) => {
  if (answerType === CONDITION_QUESTION_ANSWER_TYPE.MULTIPLE) return {};

  // return id of the first answer.
  return get(answers, '[0].id', 1);
};

/**
 * Returns only Carwiser questions.
 *
 * @param {Array} multiOfferQuestions multi-offer questions (they may include CarMax questions).
 * @returns {Array} Carwiser questions.
 */
export const filterCarwiserQuestions = multiOfferQuestions =>
  filter(multiOfferQuestions, ({ partner }) => partner === PARTNERS.CARWISER);

/**
 * Returns Carwiser condition details for Solicit More Offers payload.
 *
 * @param {Object} questionResponses Carwiser question responses in the format stored by the Trade In Appraiser.
 * @param {Array} questions multi-offer questions (they may include CarMax questions).
 * @returns {Array} Carwiser condition details for Solicit More Offers payload.
 */
const buildForInstantOfferStep = (questionResponses, questions) => {
  // this map includes nested/detail questions.
  const carwiserQuestionMap = {};

  // Need to check all questions since some Carmax parent questions may contain Carwiser child questions
  questions.forEach(question => {
    const { id, answers, partner } = question;

    if (partner === PARTNERS.CARWISER) carwiserQuestionMap[id] = question;

    forEach(answers, ({ detailQuestions }) =>
      filterCarwiserQuestions(detailQuestions).forEach(detailQuestion => {
        carwiserQuestionMap[detailQuestion.id] = detailQuestion;
      })
    );
  });

  return reduce(
    questionResponses,
    (resultArr, { desc, answers }, id) => {
      const { answerType, answers: allQuestionAnswers } = carwiserQuestionMap[id] || {};
      const isMultipleAnswer = answerType === CONDITION_QUESTION_ANSWER_TYPE.MULTIPLE;

      if (isMultipleAnswer) {
        forEach(allQuestionAnswers, ({ id: answerId, desc: answerDesc }) => {
          const isSelected = answers.some(answer => answer.id === answerId);

          resultArr.push({
            id: answerId,
            desc: answerDesc,
            answers: [isSelected ? CARWISER_MULTIPLE_ANSWERS.SELECTED : CARWISER_MULTIPLE_ANSWERS.UNSELECTED],
          });
        });
      } else {
        resultArr.push({
          id,
          desc,
          answers,
        });
      }
      return resultArr;
    },
    []
  );
};

/**
 * Returns V2 condition details for Solicit More Offers payload.
 *
 * @param {Object} questionResponses all question responses in the format stored by the Trade In Appraiser.
 * @param {String} condition vehicle condition in the format which is stored in MODS.
 * @returns {Object} V2 condition details for Solicit More Offers payload.
 */
export const buildV2ConditionDetails = (questionResponses, condition) => {
  const result = mapValues(questionResponses, ({ answers }) => ({
    answer: answers.map(({ id }) => id),
  }));
  return {
    ...result,
    // Add 'vehicle_condition' answer separately, because currently this question is NOT displayed on Step 4, but instead this data is collected on Step 3.
    [VEHICLE_CONDITION_CQ_ID]: { answer: [condition.toLowerCase()] },
  };
};

/**
 * This object provides function to build Carwiser condition details (in other words, answers for condition questions)
 * for Solicit More Offers payload.
 */
export const SOLICIT_CARWISER_CONDITION_DETAILS = {
  buildForInstantOfferStep,
};
